import {
  CandidateItemDto,
  FairCandidateItemDto,
  GetCandidatesSortPropertyDto,
  SortDirectionDto,
} from '@/api/generated';
import { TablePagination } from '@/components/table/TablePagination.comp.tsx';
import { getAppLanguage } from '@/i18n.ts';
import { useUserContext } from '@/utils/providers/userContext/useUserContext.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { CandidateProfilePanel } from '@/views/admin/candidates/CandidateProfilePanel.comp.tsx';
import { COLORS, formatNumber } from '@/views/admin/dashboard/dashboardTheme.ts';
import { AddFairCandidatesModal } from '@/views/admin/fairs/AddFairCandidatesModal.comp.tsx';
import { EditFairCandidateModal } from '@/views/admin/fairs/EditFairCandidateModal.comp.tsx';
import { FairCandidatesList } from '@/views/admin/fairs/FairCandidatesList.comp.tsx';
import { FairResultCards } from '@/views/admin/fairs/FairResultCards.comp.tsx';
import {
  useGetFairCandidates,
  useGetFairResults,
  useRefreshFairs,
} from '@/views/admin/fairs/useFairsApi.util.ts';
import { useGetCandidates } from '@/views/admin/util/useGetCandidates.util.ts';
import React, { useState } from 'react';
import { Alert } from 'react-bootstrap';
import { IoAddCircleOutline, IoClose } from 'react-icons/io5';

export const FairsTab: React.FC = () => {
  const { t } = useTranslationWithPrefix('views.admin.fairs');
  const { user } = useUserContext();
  const adminUserId = user?.userId ?? '';
  const language = getAppLanguage();
  const [selectedFair, setSelectedFair] = useState<string>();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<FairCandidateItemDto>();
  const [profile, setProfile] = useState<CandidateItemDto>();
  const [openingEmail, setOpeningEmail] = useState<string>();
  const [message, setMessage] = useState<{ text: string; variant: 'danger' | 'success' }>();
  const { isLoading: resultsLoading, results } = useGetFairResults(adminUserId);
  const { fairCandidates, isFetching } = useGetFairCandidates({
    adminUserId,
    note: selectedFair,
    page,
    pageSize,
  });
  const { getCandidates } = useGetCandidates();
  const refreshFairs = useRefreshFairs();

  const fairNames = (results ?? []).flatMap(result => (result.note ? [result.note] : []));

  const selectFair = (note: string | undefined) => {
    setPage(0);
    setSelectedFair(note);
  };

  // the profile panel works on the candidate as the candidates search gives it
  const findCandidate = async (email: string) => {
    const found = await getCandidates({
      adminUserId,
      dto: {
        email,
        sort: {
          direction: SortDirectionDto.Desc,
          property: GetCandidatesSortPropertyDto.QuizFinishedAt,
        },
      },
      page: 0,
      pageSize: 20,
    });
    return found.items.find(item => item.email.toLowerCase() === email.toLowerCase());
  };

  const openProfile = async (candidate: FairCandidateItemDto) => {
    setMessage(undefined);
    setOpeningEmail(candidate.email);
    try {
      const found = await findCandidate(candidate.email);
      if (found) {
        setProfile(found);
      } else {
        setMessage({ text: t('list.profileError'), variant: 'danger' });
      }
    } catch {
      setMessage({ text: t('list.profileError'), variant: 'danger' });
    } finally {
      setOpeningEmail(undefined);
    }
  };

  const onProfileAction = async () => {
    void refreshFairs();
    if (profile) {
      setProfile((await findCandidate(profile.email)) ?? profile);
    }
  };

  const onSaved = () => {
    setEditing(undefined);
    setMessage({ text: t('edit.success'), variant: 'success' });
  };

  const totalResults = fairCandidates?.totalResults ?? 0;
  const totalPages = fairCandidates?.totalPages ?? 0;

  return (
    <div style={{ backgroundColor: '#f4f4f4', margin: '-1.25rem -1.6875rem', minHeight: '100%' }}>
      <div className="flex flex-col gap-5 p-4 lg:p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="m-0 text-2xl font-bold" style={{ color: COLORS.ink }}>
              {t('title')}
            </h1>
            <p className="m-0 mt-1 text-sm" style={{ color: COLORS.inkSecondary }}>
              {t('subtitle')}
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)} type="button">
            <IoAddCircleOutline aria-hidden className="me-2" />
            {t('addCandidates')}
          </button>
        </div>

        {message && (
          <Alert
            className="m-0"
            dismissible
            onClose={() => setMessage(undefined)}
            variant={message.variant}
          >
            {message.text}
          </Alert>
        )}

        <section aria-labelledby="fair-results-title">
          <h2
            className="m-0 mb-3 text-lg font-bold"
            id="fair-results-title"
            style={{ color: COLORS.ink }}
          >
            {t('results.title')}
          </h2>
          <FairResultCards
            isLoading={resultsLoading}
            language={language}
            onAdd={() => setShowAdd(true)}
            onSelect={selectFair}
            results={results}
            selected={selectedFair}
          />
        </section>

        <section className="min-w-0 bg-white" style={{ border: '1px solid #e5e5e5' }}>
          <header
            className="flex flex-wrap items-center gap-3 px-4 py-3"
            style={{ borderBottom: `1px solid ${COLORS.grid}` }}
          >
            <h2 className="m-0 text-lg font-bold" style={{ color: COLORS.ink }}>
              {selectedFair ? t('list.fairTitle', { fair: selectedFair }) : t('list.allTitle')}
            </h2>
            {selectedFair && (
              <button
                className="inline-flex cursor-pointer items-center gap-1 px-2 py-1 text-sm font-bold"
                onClick={() => selectFair(undefined)}
                style={{ backgroundColor: '#fff', border: '1px solid #ccc', color: COLORS.ink }}
                type="button"
              >
                <IoClose aria-hidden />
                {t('results.allFairs')}
              </button>
            )}
            <span className="ml-auto text-sm" style={{ color: COLORS.inkSecondary }}>
              <strong style={{ color: COLORS.ink }}>{formatNumber(totalResults, language)}</strong>
            </span>
          </header>

          <FairCandidatesList
            candidates={fairCandidates?.items ?? []}
            isLoading={isFetching}
            language={language}
            onEdit={setEditing}
            onOpen={candidate => void openProfile(candidate)}
            openingEmail={openingEmail}
          />

          {totalResults > 0 && (
            <div className="px-4 pb-3">
              <TablePagination
                onNextPage={() =>
                  setPage(current => Math.min(current + 1, Math.max(totalPages - 1, 0)))
                }
                onPageChange={setPage}
                onPageSizeChange={event => {
                  setPage(0);
                  setPageSize(Number(event.target.value));
                }}
                onPreviousPage={() => setPage(current => Math.max(current - 1, 0))}
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                totalResults={totalResults}
              />
            </div>
          )}
        </section>
      </div>

      <AddFairCandidatesModal
        adminUserId={adminUserId}
        defaultFair={selectedFair}
        fairNames={fairNames}
        language={language}
        onHide={() => setShowAdd(false)}
        show={showAdd}
      />
      <EditFairCandidateModal
        adminUserId={adminUserId}
        candidate={editing}
        fairNames={fairNames}
        onHide={() => setEditing(undefined)}
        onSaved={onSaved}
      />
      {profile && (
        <CandidateProfilePanel
          candidate={profile}
          language={language}
          onActionSuccess={() => void onProfileAction()}
          onClose={() => setProfile(undefined)}
        />
      )}
    </div>
  );
};
